import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
} from 'react';
import { ReactEditor, useSlateStatic } from 'slate-react';
import { Menu } from './Menu/Menu';
import { Separator } from './Menu/Menu.styled';
import { RichTextButton } from './RichTextButton/RichTextButton';
import { LinkButton } from './LinkButton/LinkButton';
import { useDebouncedEffect } from '../../lib/useDebouncedEffect';
import { HeaderButton } from './HeaderButton/HeaderButton';
import { TextColourButton } from './TextColourButton/TextColourButton';
import { Portal } from '../Portal/Portal';
import {
  hasSelection,
  selectionIncludesElementOfType,
} from '../../lib/queries';
import { computePosition, hiddenPosition } from '../../lib/position';
import { debounce } from '../../lib/debounce';
import {
  richTextCommandsGroup1,
  richTextCommandsGroup2,
  textColourCommand,
} from '../../plugins/richtext/config';
import { latexType } from '../../plugins/latex/latexType';
import { RichTextCommand } from '../../plugins/richtext/types';
import { codeType } from '../../plugins/code/codeType';
import { useLastActiveSelection } from '../../lib/useLastActiveSelection';
import { ListButton } from './ListButton/ListButton';

export const HoveringToolbar = () => {
  const [current, setCurrent] = useState<HTMLDivElement>();
  const [isLinkPopupOpen, setIsLinkPopupOpen] = useState(false);
  const handleOpenLinkPopup = () => setIsLinkPopupOpen(true);
  const handleCloseLinkPopup = () => setIsLinkPopupOpen(false);
  const [position, setPosition] = useState(hiddenPosition);
  const [scrollY, setScrollY] = useState(window.scrollY);
  const [isDismissed, setIsDismissed] = useState(false);
  const focusRef = useRef<HTMLButtonElement>();
  const headerMenuRef = useRef<{ getIsOpen: () => boolean }>();
  const colourMenuRef = useRef<{ getIsOpen: () => boolean }>();

  const editor = useSlateStatic();
  const { selection } = editor;

  const visible = useMemo(
    () =>
      hasSelection(editor) &&
      ReactEditor.isFocused(editor) &&
      !selectionIncludesElementOfType(editor, latexType, codeType) &&
      !isDismissed,
    // In this case we want to intentionally recompute visible
    // hence there are more dependencies defined for this memoized value
    // than required by eslint
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editor, selection, current, isDismissed]
  );

  useDebouncedEffect(
    () => {
      if (visible && current != null) {
        setPosition(computePosition(current));
      }
    },
    300,
    [selection, current, scrollY]
  );

  // Reset dismissed status when selection changes
  useEffect(
    () => {
      if (isDismissed) {
        setIsDismissed(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selection]
  );

  useEffect(() => {
    const onScroll = debounce(() => {
      if (ReactEditor.isFocused(editor)) {
        setScrollY(window.scrollY);
      }
    });

    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  });

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && visible) {
        event.stopPropagation();
        event.preventDefault();
        setIsDismissed(true);
        ReactEditor.focus(editor);
      }

      if (
        event.key === 'Tab' &&
        visible &&
        !current?.contains(document.activeElement) &&
        !headerMenuRef?.current?.getIsOpen() &&
        !colourMenuRef?.current?.getIsOpen()
      ) {
        if (document.activeElement !== focusRef.current) {
          event.stopPropagation();
          event.preventDefault();
          focusRef?.current?.focus();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);

    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [editor, visible, current]);

  const ref = useCallback((node) => {
    if (node !== null) {
      setCurrent(node);
    }
  }, []);

  const { lastActiveSelection } = useLastActiveSelection();

  const renderRichTextCommand = (command: RichTextCommand) => (
    <RichTextButton
      key={command.label}
      label={command.label}
      richTextMark={command.richTextMark}
      disabled={command.disabled(editor)}
      icon={command.icon}
      hotkeys={command.hotkeys}
      lastActiveSelection={lastActiveSelection}
    />
  );

  // visible is set based on whether there is a selection. However in Safari the selection
  // is lost when the focus shifts to the link popup, which would close the hovering toolbar
  // and since this is the parent of the link popup, also close the link popup which has just
  // been focussed
  if (!visible && !isLinkPopupOpen) {
    return null;
  }

  return (
    <Portal>
      <Menu ref={ref} position={position}>
        <HeaderButton
          ref={focusRef}
          menuRef={headerMenuRef}
          lastActiveSelection={lastActiveSelection}
        />
        <ListButton
          lastActiveSelection={lastActiveSelection}
          label="Bulleted list"
          buttonType="Unordered"
        />
        <ListButton
          lastActiveSelection={lastActiveSelection}
          label="Numbered list"
          buttonType="Ordered"
        />
        <Separator />
        {richTextCommandsGroup1.map(renderRichTextCommand)}
        <TextColourButton
          ref={colourMenuRef}
          lastActiveSelection={lastActiveSelection}
          label={textColourCommand.label}
          hotkeys={textColourCommand.hotkeys}
        />
        <LinkButton
          lastActiveSelection={lastActiveSelection}
          isOpen={isLinkPopupOpen}
          onOpen={handleOpenLinkPopup}
          onClose={handleCloseLinkPopup}
        />
        {richTextCommandsGroup2.map(renderRichTextCommand)}
      </Menu>
    </Portal>
  );
};
